import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  ChatBubbleBottomCenterTextIcon,
  SparklesIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import ValuationChatWindow from './ValuationChatWindow';
import { formatCurrency } from '../../../lib/format';
import Button from '../../../../../ui/canon/Button';
import { pctSymbol } from '../../../utils/formatters';

const ValuationAgentWidget = ({ user, currentUser, company, features, valuationData, dcfParams, onDcfUpdate }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme, company: themeCompany } = useSuccessionTheme();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null); // Persist conversation ID across opens/closes
  const [confidenceScore, setConfidenceScore] = useState(null);
  const [confidenceFactors, setConfidenceFactors] = useState([]);
  const [topRecommendation, setTopRecommendation] = useState(null);
  const [industryBenchmark, setIndustryBenchmark] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if valuation agent is enabled using features array
  const isEnabled = Array.isArray(features) &&
                   features.includes('valuation_agent') &&
                   features.includes('agent_widgets');

  // Load initial agent analysis
  useEffect(() => {
    if (!isEnabled) return;
    
    const loadAgentAnalysis = async () => {
      try {
        setIsLoading(true);
        
        // Create a basic ValuationAgent analysis using existing user data
        const response = await analyzeCurrentValuation(user);
        setConfidenceScore(response.confidence_score);
        setConfidenceFactors(response.confidence_factors);
        setTopRecommendation(response.top_recommendation);
        setIndustryBenchmark(response.industry_benchmark);
      } catch (error) {
        console.error('Error loading valuation analysis:', error);
        // Fallback to basic analysis
        setConfidenceScore({ score: 45, level: 'Low' });
        setConfidenceFactors([]);
        setTopRecommendation({
          title: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.add_historical_revenue' }),
          description: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.missing_revenue_history' })
        });
        setIndustryBenchmark(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadAgentAnalysis();
  }, [user, isEnabled]);

  // Listen for custom events to open valuation chat from AI Assistant
  useEffect(() => {
    const handleOpenValuationChat = (event) => {
      console.log('Received openValuationChat event from:', event.detail?.source);
      if (isEnabled) {
        setIsChatOpen(true);
      }
    };

    window.addEventListener('openValuationChat', handleOpenValuationChat);

    return () => {
      window.removeEventListener('openValuationChat', handleOpenValuationChat);
    };
  }, [isEnabled]);

  // Enhanced client-side analysis with detailed confidence factors
  // NOTE: This should match the backend calculation in ValuationChatAgent#calculate_confidence_score
  const analyzeCurrentValuation = async (userData) => {
    // Check company capabilities through features array
    const safeFeatures = Array.isArray(features) ? features : [];
    const allowsGrowthRates = safeFeatures.includes('allow_user_to_specify_revenue_growth_rate'); // When true, users can specify growth rates
    // Historical data is only relevant when:
    // 1. Users CAN specify growth rates (allowsGrowthRates = true), AND
    // 2. User chose NOT to use growth rates (use_revenue_growth_rate = false)
    // If users cannot specify growth rates at all, then historical data is not a factor they can control
    const allowsHistoricalData = allowsGrowthRates && !userData?.use_revenue_growth_rate;
    const allowsExpenseInput = true; // Generally allow expense input unless specifically restricted

    // DEBUG: Log what we're seeing
    console.error('VALUATION DEBUG:', {
      features: safeFeatures,
      hasGrowthFlag: safeFeatures.includes('allow_user_to_specify_revenue_growth_rate'),
      allowsGrowthRates,
      allowsHistoricalData,
      allowsExpenseInput,
      // Check for related flags
      hasHistoricalFlag: safeFeatures.includes('compact_historical_revenue_growth_rate'),
      revenueFlagsFound: safeFeatures.filter(f => f && f.includes('revenue')),
      historicalFlagsFound: safeFeatures.filter(f => f && f.includes('historical'))
    });


    // Check for revenue breakdown (DCF fields)
    const hasRevenue = (userData?.fee_based_revenue > 0) ||
                      (userData?.trailer_revenue > 0) ||
                      (userData?.transactional_revenue > 0) ||
                      (userData?.direct_revenue > 0);
    const hasExpenses = userData?.gross_expenses > 0;
    const hasHistoricalData = userData?.year_1_gross > 0 || userData?.year_2_gross > 0;
    const hasGrowthRates = userData?.revenue_growth_rate > 0 || userData?.expense_growth_rate > 0;
    const hasBranchPayout = userData?.branch_net_payout > 0;

    // Calculate total annual revenue for consistency checks
    const totalRevenue = (userData?.fee_based_revenue || 0) +
                        (userData?.trailer_revenue || 0) +
                        (userData?.transactional_revenue || 0) +
                        (userData?.direct_revenue || 0);

    const historicalRevenue = userData?.year_1_gross || 0;
    const isRevenueConsistent = !historicalRevenue || Math.abs(totalRevenue - historicalRevenue) < totalRevenue * 0.15;

    // Build detailed confidence factors (only include factors for available features)
    const factors = [
      // Revenue breakdown is always required
      {
        name: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.revenue_breakdown' }),
        status: hasRevenue ? 'complete' : 'missing',
        description: hasRevenue ? intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.revenue_sources_identified' }) : intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.missing_revenue_breakdown' }),
        icon: hasRevenue ? 'check' : 'x'
      }
    ];

    // Only include historical data factor if the company allows it
    if (allowsHistoricalData) {
      factors.push({
        name: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.historical_data' }),
        status: hasHistoricalData ? 'complete' : 'missing',
        description: hasHistoricalData ? intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.historical_trends_available' }) : intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.historical_data_needed' }),
        icon: hasHistoricalData ? 'check' : 'clock'
      });
    }

    // Only include expenses factor if the company allows expense input
    if (allowsExpenseInput) {
      factors.push({
        name: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.operating_expenses' }),
        status: hasExpenses ? 'complete' : 'missing',
        description: hasExpenses ? intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.expense_data_present' }) : intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.operating_expenses_needed' }),
        icon: hasExpenses ? 'check' : 'x'
      });
    }

    // Only include growth rates factor if the company allows user-specified growth rates
    if (allowsGrowthRates) {
      factors.push({
        name: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.growth_assumptions' }),
        status: hasGrowthRates ? 'complete' : 'missing',
        description: hasGrowthRates ? intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.growth_rates_defined' }) : intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.growth_projections_needed' }),
        icon: hasGrowthRates ? 'check' : 'x'
      });
    }

    // Data consistency check (always relevant if we have both current and historical revenue)
    if (allowsHistoricalData && hasHistoricalData) {
      factors.push({
        name: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.data_consistency' }),
        status: isRevenueConsistent ? 'complete' : 'warning',
        description: isRevenueConsistent ? intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.revenue_data_consistent' }) : intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.revenue_mismatch' }),
        icon: isRevenueConsistent ? 'check' : 'warning'
      });
    }

    // Calculate score based on factors (matching backend logic)
    const missingCount = factors.filter(f => f.status === 'missing').length;
    const warningCount = factors.filter(f => f.status === 'warning').length;

    // Base completeness score (matching backend calculate_data_completeness_score)
    let base_score = 100;
    base_score -= (missingCount * 15); // -15 points per critical missing item
    base_score -= (warningCount * 5);  // -5 points per warning
    base_score = Math.max(base_score, 0); // Ensure non-negative

    // Revenue diversification bonus (matching backend)
    const revenueTypes = [userData?.fee_based_revenue, userData?.trailer_revenue,
                          userData?.transactional_revenue, userData?.direct_revenue];
    const activeSources = revenueTypes.filter(rev => rev > 0).length;
    const diversificationBonus = Math.min(activeSources * 2, 10); // Max 10 points

    // Historical data depth bonus (only if company allows historical data)
    let historicalBonus = 0;
    if (allowsHistoricalData) {
      const historicalFields = [userData?.year_1_gross, userData?.year_2_gross,
                               userData?.year_3_gross, userData?.year_4_gross, userData?.year_5_gross];
      const historicalCount = historicalFields.filter(val => val > 0).length;
      historicalBonus = Math.min(historicalCount * 3, 15); // Max 15 points
    }

    // Final score calculation (matching backend)
    const finalScore = Math.min(base_score + diversificationBonus + historicalBonus, 100);
    const score = Math.round(finalScore);
    const level = score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low';

    // Determine top recommendation based on first missing factor
    const missingFactors = factors.filter(f => f.status === 'missing');
    let topRec = null;

    if (missingFactors.length > 0) {
      const topMissing = missingFactors[0];
      topRec = {
        title: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.add_recommendation' }, { item: topMissing.name }),
        description: topMissing.description
      };
    } else {
      // All core data present, suggest optimization
      topRec = {
        title: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.optimize_accuracy' }),
        description: intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.consider_professional_review' })
      };
    }

    return {
      confidence_score: { score: Math.round(score), level },
      confidence_factors: factors,
      top_recommendation: topRec,
      industry_benchmark: null // No real industry data available
    };
  };

  const handleChatOpen = () => {
    console.log('=== OPENING VALUATION CHAT ===');
    console.log('dcfParams:', dcfParams);
    console.log('dcfParams keys:', dcfParams ? Object.keys(dcfParams) : 'null');
    console.log('dcfParams.value_of_practice:', dcfParams?.value_of_practice);
    console.log('valuationData:', valuationData);
    console.log('valuationData.valueOfPractice:', valuationData?.valueOfPractice);
    console.log('user:', user);
    console.log('==============================');
    setIsChatOpen(true);
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
  };

  // If not enabled, show Sum of Cash Flows instead
  if (!isEnabled) {
    return (
      <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-succession-primary rounded-md p-3">
            <CurrencyDollarIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-succession-text-secondary truncate transition-colors duration-300">
                {intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.valuation_method' })}
              </dt>
              <dd>
                <div className="text-lg font-semibold text-succession-primary transition-colors duration-300">
                  {valuationData?.sumDiscountedCashFlows ? formatCurrency(valuationData.sumDiscountedCashFlows) : '$0'}
                </div>
              </dd>
            </dl>
          </div>
        </div>
      </div>
    );
  }

  // Get confidence color classes
  const getConfidenceClasses = (level) => {
    switch (level) {
      case 'High':
        return { 
          color: 'text-succession-success', 
          bgColor: 'bg-succession-success-10',
          icon: ChartBarIcon
        };
      case 'Medium':
        return { 
          color: 'text-succession-warning', 
          bgColor: 'bg-succession-warning-10',
          icon: ChartBarIcon
        };
      default:
        return { 
          color: 'text-succession-error', 
          bgColor: 'bg-succession-error-10',
          icon: ExclamationTriangleIcon
        };
    }
  };

  const confidenceClasses = confidenceScore ? getConfidenceClasses(confidenceScore.level) : {};

  // Helper function to get icon component for confidence factors
  const getFactorIcon = (iconType, status) => {
    const baseClasses = "h-4 w-4";
    const colorClasses = status === 'complete' ? 'text-succession-success' :
                        status === 'warning' ? 'text-succession-warning' :
                        'text-succession-error';

    switch (iconType) {
      case 'check':
        return <CheckCircleIcon className={`${baseClasses} ${colorClasses}`} />;
      case 'clock':
        return <ClockIcon className={`${baseClasses} ${colorClasses}`} />;
      case 'warning':
        return <ExclamationTriangleIcon className={`${baseClasses} ${colorClasses}`} />;
      default:
        return <XCircleIcon className={`${baseClasses} ${colorClasses}`} />;
    }
  };

  // Confidence meter component
  const ConfidenceMeter = ({ score, level }) => {
    const percentage = Math.min(score, 100);
    const meterColor = level === 'High' ? 'bg-succession-success' :
                      level === 'Medium' ? 'bg-succession-warning' :
                      'bg-succession-error';

    return (
      <div className="w-full">
        <div className="flex justify-between text-xs text-succession-text-secondary mb-1">
          <span>{intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.confidence' })}</span>
          <span>{score}{pctSymbol()}</span>
        </div>
        <div className="w-full bg-succession-bg-tertiary rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${meterColor}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Valuation Agent Widget - Matching other widgets format */}
      <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-succession-accent rounded-md p-3">
            <ChartBarIcon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-succession-text-secondary truncate transition-colors duration-300">
                {intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.title' })}
              </dt>
              <dd>
                <div className="text-lg font-semibold transition-colors duration-300">
                  <button
                    onClick={handleChatOpen}
                    disabled={isLoading}
                    className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded bg-succession-primary text-white shadow-md transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      color: 'white'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = 'brightness(0.85)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'none';
                      e.currentTarget.style.color = 'white';
                    }}
                  >
                    <ChatBubbleBottomCenterTextIcon className="w-4 h-4 mr-2" style={{ color: 'white' }} />
                    <span style={{ color: 'white' }}>{intl.formatMessage({ id: 'succession.agent_system.valuation_agent_widget.ask_ai' })}</span>
                  </button>
                </div>
              </dd>
            </dl>
          </div>
        </div>
      </div>

      {/* Chat Window Modal */}
      {isChatOpen && (
        <ValuationChatWindow
          user={user}
          currentUser={currentUser}
          onClose={handleChatClose}
          initialConfidence={confidenceScore}
          initialRecommendation={topRecommendation}
          pageContext="valuation"
          dcfId={dcfParams?.dcf_id || valuationData?.id || user?.dcf_id}
          conversationId={conversationId} // Pass persisted conversation ID
          onConversationIdChange={setConversationId} // Callback to update conversation ID
          clientState={{
            // Use dcfParams if available (from recalculation), otherwise fall back to user data
            fee_based_revenue: parseFloat(dcfParams?.fee_based_revenue || user?.fee_based_revenue) || 0,
            trailer_revenue: parseFloat(dcfParams?.trailer_revenue || user?.trailer_revenue) || 0,
            transactional_revenue: parseFloat(dcfParams?.transactional_revenue || user?.transactional_revenue) || 0,
            direct_revenue: parseFloat(dcfParams?.direct_revenue || user?.direct_revenue) || 0,
            year_1_gross: parseFloat(dcfParams?.year_1_gross || user?.year_1_gross) || 0,
            year_2_gross: parseFloat(dcfParams?.year_2_gross || user?.year_2_gross) || 0,
            year_3_gross: parseFloat(dcfParams?.year_3_gross || user?.year_3_gross) || 0,
            year_4_gross: parseFloat(dcfParams?.year_4_gross || user?.year_4_gross) || 0,
            year_5_gross: parseFloat(dcfParams?.year_5_gross || user?.year_5_gross) || 0,
            gross_expenses: parseFloat(dcfParams?.gross_expenses || user?.gross_expenses) || 0,
            expense_growth_rate: parseFloat(dcfParams?.expense_growth_rate || user?.expense_growth_rate) || 0.03,
            revenue_growth_rate: parseFloat(dcfParams?.revenue_growth_rate || user?.revenue_growth_rate) || 0.03,
            branch_net_payout: parseFloat(dcfParams?.branch_net_payout || user?.branch_net_payout) || 0.85,
            use_benchmark_expense_percentage: Boolean(dcfParams?.use_benchmark_expense_percentage ?? user?.use_benchmark_expense_percentage),
            use_revenue_growth_rate: Boolean(dcfParams?.use_revenue_growth_rate ?? user?.use_revenue_growth_rate),
            // Use the current valuation value from valuationData or dcfParams
            value_of_practice: parseFloat(dcfParams?.value_of_practice || valuationData?.valueOfPractice) || 0
          }}
          onDcfUpdate={onDcfUpdate}
        />
      )}
    </>
  );
};

export default ValuationAgentWidget;